import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { CreditCard, ArrowUp, PlusCircle, Buildings, CheckCircle, Sparkle, EnvelopeSimple, Lock, Receipt, DownloadSimple, ArrowSquareOut } from "@phosphor-icons/react";
import AdfinCheckout from "@/components/AdfinCheckout";

const PROVIDER = process.env.REACT_APP_PAYMENTS_PROVIDER || "adfin";
const BILLING_ENABLED = process.env.REACT_APP_BILLING_ENABLED !== "false";
const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB")}`;
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
};

export default function SubscriptionPanel() {
  const [cfg, setCfg] = useState(null);
  const [sub, setSub] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [planId, setPlanId] = useState(null);
  const [packs, setPacks] = useState(0);
  const [saving, setSaving] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [changing, setChanging] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showAdfin, setShowAdfin] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const [showUpsell, setShowUpsell] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const load = () => {
    Promise.all([api.get("/billing/plans"), api.get("/billing/subscription")])
      .then(([{ data: c }, { data: s }]) => {
        setCfg(c);
        setSub(s);
        setCycle(s.billing_cycle || "monthly");
        setPlanId(s.plan?.id);
        setPacks(s.growth_packs || 0);
      })
      .catch(() => {});
    api.get("/billing/invoices").then(({ data }) => setInvoices(data.invoices || [])).catch(() => setInvoices([]));
  };
  useEffect(load, []);

  // Handle return from Stripe Checkout / portal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (params.get("checkout") === "cancelled") {
      toast.message("Checkout cancelled");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sid) return;
    setVerifying(true);
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/checkout/status/${sid}`);
        if (data.subscribed || data.payment_status === "paid" || data.status === "complete") {
          toast.success("Subscription active — thank you!");
          setVerifying(false);
          window.history.replaceState({}, "", window.location.pathname);
          load();
          return;
        }
      } catch (e) { /* keep polling */ }
      if (++attempts >= 6) {
        setVerifying(false);
        window.history.replaceState({}, "", window.location.pathname);
        load();
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  if (!cfg || !sub) return null;

  const plan = cfg.plans.find((p) => p.id === planId) || sub.plan;
  const gp = cfg.growth_pack;
  const used = sub.used || 0;
  const supportsPack = !!plan?.growth_pack;
  const enterprise = !!plan?.enterprise;
  const pastDue = sub.account_status === "past_due" || sub.stripe_status === "past_due";

  const baseCompanies = plan?.companies || 0;
  const allowance = enterprise ? null : baseCompanies + packs * gp.companies;
  const over = !enterprise && allowance != null && used > allowance;
  const remaining = !enterprise && allowance != null ? Math.max(0, allowance - used) : null;
  const nearLimit = !enterprise && allowance != null && !over && (sub.near_limit || remaining <= 1 || (allowance && used / allowance >= 0.8));
  const downgradeBlocked = !enterprise && allowance != null && used > allowance;

  const price = enterprise ? null : (cycle === "annual" ? plan.annual : plan.monthly) + packs * (cycle === "annual" ? gp.annual : gp.monthly);

  // Non-owner team member: read-only "managed by" view
  if (!sub.is_owner) {
    return (
      <motion.div data-testid="subscription-managed-by"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        className="card-surface overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center bg-navy/[0.06]">
            <Lock size={18} weight="fill" className="text-navy/70" />
          </span>
          <div>
            <h2 className="font-heading font-bold text-lg text-navy tracking-tight">Subscription</h2>
            <p className="text-xs text-navy/70">Managed centrally for your practice.</p>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-baseline justify-between gap-3 mb-5">
            <div>
              <div className="overline text-navy/65 mb-1">Current plan</div>
              <div className="font-heading font-black text-2xl text-navy tracking-tight" data-testid="managed-plan-name">{plan?.name}</div>
            </div>
            <div className="text-sm text-navy/70" data-testid="managed-usage">
              <span className="font-semibold text-navy">{used}</span> connected
              {!enterprise && allowance != null && <> of <span className="font-semibold text-navy">{allowance}</span></>}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 flex items-start gap-3">
            <EnvelopeSimple size={20} weight="fill" className="text-orangeink shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm text-navy">
                This subscription is managed by{" "}
                <a href={`mailto:${sub.subscriber_email}`} className="font-semibold text-orangeink hover:underline" data-testid="managed-by-email">{sub.subscriber_email}</a>.
              </p>
              <p className="text-xs text-navy/70 mt-1">Contact them for any subscription or billing questions.</p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  const dirty = planId !== sub.plan?.id || (cycle || "monthly") !== (sub.billing_cycle || "monthly") || packs !== (sub.growth_packs || 0);

  const save = async (override) => {
    const body = {
      plan_id: override?.plan_id || planId,
      billing_cycle: cycle,
      growth_packs: override?.growth_packs != null ? override.growth_packs : packs,
    };
    setSaving(true);
    try {
      const { data } = await api.post("/billing/subscription", body);
      setSub(data);
      setPlanId(data.plan?.id);
      setPacks(data.growth_packs || 0);
      setCycle(data.billing_cycle || "monthly");
      toast.success("Subscription updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update subscription");
    } finally {
      setSaving(false);
    }
  };

  const pollCheckout = (sid, popup) => {
    setVerifying(true);
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts++;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sid}`);
        if (data.subscribed || data.payment_status === "paid" || data.status === "complete") {
          clearInterval(iv);
          if (popup && !popup.closed) popup.close();
          setVerifying(false);
          setCheckingOut(false);
          toast.success("Subscription active — thank you!");
          load();
          return;
        }
      } catch (e) { /* keep polling */ }
      if (popup && popup.closed) {
        clearInterval(iv);
        setVerifying(false);
        setCheckingOut(false);
        toast.message("Checkout window closed");
        load();
        return;
      }
      if (attempts >= 150) { // ~5 min safety stop
        clearInterval(iv);
        setVerifying(false);
        setCheckingOut(false);
        if (popup && !popup.closed) popup.close();
      }
    }, 2000);
  };

  const checkout = async (chosenCycle) => {
    const useCycle = typeof chosenCycle === "string" ? chosenCycle : cycle;
    setCheckingOut(true);
    // Open the popup synchronously (inside the click) so browsers don't block it.
    const w = 520, h = 760;
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    const popup = window.open("about:blank", "wwl_checkout",
      `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId, billing_cycle: useCycle, growth_packs: packs,
        origin_url: window.location.origin,
      });
      if (!data.url) throw new Error("No checkout URL");
      if (popup && !popup.closed) {
        popup.location.href = data.url;
        pollCheckout(data.session_id, popup);
      } else {
        // Popup blocked — fall back to a full-page redirect.
        window.location.href = data.url;
      }
    } catch (e) {
      if (popup && !popup.closed) popup.close();
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setCheckingOut(false);
    }
  };

  const resendReceipt = async (invoiceId) => {
    setResendingId(invoiceId);
    try {
      const { data } = await api.post(`/billing/invoices/${invoiceId}/resend`);
      toast.success(`Receipt sent to ${data.email || "your billing email"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend receipt");
    } finally {
      setResendingId(null);
    }
  };

  // Show the "pay annually & save" nudge before sending a monthly subscriber to checkout.
  const onSubscribeClick = () => {
    if (PROVIDER === "adfin") { setShowAdfin(true); return; }
    if (!enterprise && cycle === "monthly" && (plan?.monthly || 0) > 0) { setShowUpsell(true); return; }
    checkout();
  };

  const openPortal = async (flow) => {
    const wantFlow = typeof flow === "string" ? flow : "";
    setPortalLoading(true);
    const w = 520, h = 760;
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    const popup = window.open("about:blank", "wwl_portal",
      `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin, flow: wantFlow });
      if (!data.url) throw new Error("No portal URL");
      if (popup && !popup.closed) {
        popup.location.href = data.url;
        const iv = setInterval(async () => {
          if (!popup.closed) return;
          clearInterval(iv);
          setPortalLoading(false);
          if (wantFlow === "payment_method_update") {
            setRetrying(true);
            try {
              const { data: r } = await api.post("/billing/retry-payment");
              if (r.retried) toast.success("Payment retried — thank you!");
              else toast.message("Card updated — we'll retry your payment shortly.");
            } catch (e) {
              toast.message(e?.response?.data?.detail || "Card updated — we'll retry your payment shortly.");
            } finally { setRetrying(false); }
          }
          load();
        }, 1500);
      } else {
        window.location.href = data.url; // popup blocked — fall back
      }
    } catch (e) {
      if (popup && !popup.closed) popup.close();
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  const changePlan = async () => {
    setChanging(true);
    try {
      await api.post("/billing/change-plan", { plan_id: planId, billing_cycle: cycle, growth_packs: packs });
      toast.success("Plan updated — the difference is prorated on your next invoice");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update plan");
    } finally {
      setChanging(false);
    }
  };

  const switchToAnnual = async () => {
    setChanging(true);
    try {
      await api.post("/billing/change-plan", { plan_id: sub.plan?.id, billing_cycle: "annual", growth_packs: sub.growth_packs || 0 });
      toast.success("Switched to annual billing — you're now saving ~2 months a year 🎉");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch to annual");
    } finally {
      setChanging(false);
    }
  };

  const cancelAdfin = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of your current billing period, and no further payments will be taken.")) return;
    setCancelling(true);
    try {
      await api.post("/adfin/cancel");
      toast.success("Subscription will cancel at the end of your billing period");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  const resumeAdfin = async () => {
    setCancelling(true);
    try {
      await api.post("/adfin/resume");
      toast.success("Subscription resumed — thanks for staying with us!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume subscription");
    } finally {
      setCancelling(false);
    }
  };

  // server-computed recommendation for the *saved* plan when at/over capacity
  const recOption = sub.recommended
    ? (sub.options || []).find((o) => o.type === sub.recommended)
    : null;

  const applyRecommendation = () => {
    if (!recOption) return;
    if (recOption.type === "growth_pack") save({ growth_packs: (sub.growth_packs || 0) + 1 });
    else if (recOption.type === "upgrade") save({ plan_id: recOption.plan_id, growth_packs: 0 });
    else window.location.href = "mailto:hello@workwithlottie.com?subject=Enterprise%20enquiry";
  };

  return (
    <motion.div data-testid="subscription-panel"
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="card-surface overflow-hidden mb-8">
      <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(244,122,36,0.12)" }}>
          <CreditCard size={18} weight="fill" style={{ color: "#F37A24" }} />
        </span>
        <div>
          <h2 className="font-heading font-bold text-lg text-navy tracking-tight">Your subscription</h2>
          <p className="text-xs text-navy/70">Choose the plan that fits your practice. Billing starts after your free trial.</p>
        </div>
        {sub.auto_selected && (
          <span className="ml-auto text-[0.6rem] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-orange/10 text-orangeink" data-testid="sub-auto-badge">
            Auto-selected
          </span>
        )}
      </div>

      <div className="p-6">
        {/* Current plan summary band */}
        <div className="flex flex-wrap items-end justify-between gap-4 pb-5 border-b border-slate-100">
          <div>
            <div className="overline text-navy/65 mb-1">Current plan</div>
            <div className="font-heading font-black text-3xl text-navy tracking-tight" data-testid="sub-current-plan">{plan?.name}</div>
            {sub.growth_packs > 0 && gp && (
              <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-navy/70 bg-slate-100 rounded-full px-2.5 py-1" data-testid="sub-current-packs">
                <PlusCircle size={13} weight="fill" style={{ color: "#F37A24" }} />
                {sub.growth_packs} × {gp.companies}-Company Pack
              </div>
            )}
            <div className="mt-3 flex items-center gap-2 text-sm text-navy/70" data-testid="sub-usage">
              <Buildings size={16} weight="fill" className="text-navy/65" />
              <span className="font-semibold text-navy">{used}</span> connected
              {!enterprise && <> of <span className="font-semibold text-navy">{allowance}</span> included</>}
              {enterprise && <span className="text-navy/70">· unlimited</span>}
            </div>
            {!enterprise && (
              <div className="mt-2 h-2.5 w-56 max-w-full rounded-full bg-slate-100 overflow-hidden" data-testid="sub-usage-bar">
                <div className="h-full rounded-full transition-all" style={{
                  width: `${Math.min(100, allowance ? (used / allowance) * 100 : 0)}%`,
                  background: over ? "#E5484D" : "#F37A24",
                }} />
              </div>
            )}
          </div>
          {!enterprise && (
            <div className="text-right">
              <div className="font-heading font-black text-2xl text-navy" data-testid="sub-current-price">{gbp(price)}</div>
              <div className="text-xs text-navy/65">/{cycle === "annual" ? "year" : "month"} · excl. VAT</div>
              {cycle === "annual" && plan.monthly > 0 && (
                <div className="inline-flex items-center gap-1 text-[0.7rem] font-bold mt-1.5 px-2 py-0.5 rounded-full" data-testid="sub-annual-save"
                  style={{ background: "rgba(0,168,89,0.12)", color: "#00A859" }}>
                  You save {gbp((plan.monthly * 12 - plan.annual) + packs * (gp.monthly * 12 - gp.annual))}/yr
                </div>
              )}
              {cycle !== "annual" && plan.monthly > 0 && (
                <button onClick={() => setCycle("annual")} data-testid="sub-switch-annual"
                  className="block ml-auto text-[0.7rem] text-orangeink hover:underline mt-1.5">
                  Switch to annual & save {gbp(plan.monthly * 2)}/yr
                </button>
              )}
            </div>
          )}
        </div>

        {over && recOption ? (
          <div className="mt-5 rounded-xl border border-warn/40 bg-warn/[0.06] p-4" data-testid="sub-recommendation">
            <div className="flex items-start gap-2.5">
              <Sparkle size={18} weight="fill" className="text-warn shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-navy">You've exceeded your included companies</div>
                <p className="text-xs text-navy/65 mt-1">
                  {recOption.type === "growth_pack" && (
                    <>Best value: add a {gp.companies}-Company Growth Pack for {gbp(cycle === "annual" ? recOption.extra_annual : recOption.extra_monthly)}/{cycle === "annual" ? "yr" : "mo"} — covers up to {recOption.new_allowance} companies.</>
                  )}
                  {recOption.type === "upgrade" && (
                    <>Best value: upgrade to <b>{recOption.plan_name}</b> (+{gbp(cycle === "annual" ? recOption.extra_annual : recOption.extra_monthly)}/{cycle === "annual" ? "yr" : "mo"}) — covers up to {recOption.new_allowance} companies.</>
                  )}
                  {recOption.type === "enterprise" && (<>You're at Enterprise scale — let's tailor a plan for you.</>)}
                </p>
                {recOption.type === "upgrade" && recOption.note && (
                  <p className="text-[0.7rem] text-navy/70 mt-1" data-testid="sub-upgrade-why">{recOption.note}</p>
                )}
                <button onClick={applyRecommendation} disabled={saving} data-testid="sub-apply-recommendation"
                  className="mt-3 inline-flex items-center gap-1.5 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-transform hover:scale-[1.02] disabled:opacity-60"
                  style={{ background: "#F37A24" }}>
                  {recOption.type === "growth_pack" && <><PlusCircle size={15} weight="bold" /> Add Growth Pack</>}
                  {recOption.type === "upgrade" && <><ArrowUp size={15} weight="bold" /> Upgrade to {recOption.plan_name}</>}
                  {recOption.type === "enterprise" && <>Contact Sales</>}
                </button>
              </div>
            </div>
          </div>
        ) : nearLimit ? (
          <div className="mt-5 rounded-xl border border-warn/40 bg-warn/[0.06] p-4" data-testid="sub-near-limit">
            <div className="flex items-start gap-2.5">
              <Sparkle size={18} weight="fill" className="text-warn shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-navy">
                  {remaining === 0 ? "You've used all your included companies" : `Only ${remaining} compan${remaining === 1 ? "y" : "ies"} left on your plan`}
                </div>
                <p className="text-xs text-navy/65 mt-1">
                  You're using {used} of {allowance}. {supportsPack ? "Add a Growth Pack" : "Upgrade your plan"} now to avoid hitting the limit when you connect your next client.
                </p>
                {sub.recommended && (
                  <button onClick={applyRecommendation} disabled={saving} data-testid="sub-nudge-upgrade"
                    className="mt-3 inline-flex items-center gap-1.5 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-transform hover:scale-[1.02] disabled:opacity-60"
                    style={{ background: "#F37A24" }}>
                    <ArrowUp size={15} weight="bold" /> {supportsPack ? "Add capacity" : "Upgrade plan"}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : !over && !enterprise ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-positive" data-testid="sub-within-allowance">
            <CheckCircle size={16} weight="fill" /> Within your plan allowance
          </div>
        ) : null}

        {/* Change plan */}
        <div className="mt-6">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="overline text-navy/65">Change plan</div>
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl w-fit" data-testid="sub-cycle-toggle">
              {["monthly", "annual"].map((c) => (
                <button key={c} onClick={() => setCycle(c)} data-testid={`sub-cycle-${c}`}
                  className={`px-4 py-1.5 text-sm font-medium rounded-lg capitalize transition-all ${cycle === c ? "bg-white text-navy shadow-card" : "text-navy/70 hover:text-navy"}`}>
                  {c}{c === "annual" && <span className="ml-1.5 text-[0.6rem] font-bold text-positive">−2mo</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Plan grid — no scroll */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2.5" data-testid="sub-plan-list">
            {cfg.plans.map((p) => {
              const active = p.id === planId;
              const tooSmall = p.companies != null && p.companies < used;
              return (
                <button key={p.id} onClick={() => { setPlanId(p.id); if (!p.growth_pack) setPacks(0); }}
                  data-testid={`sub-plan-${p.id}`}
                  className={`relative flex flex-col gap-1 rounded-xl border px-3.5 py-3 text-left transition-all ${active ? "border-[#F37A24] ring-1 ring-[#F37A24] bg-[#F37A24]/[0.04]" : "border-slate-200 hover:border-[#F37A24]/50"}`}>
                  {active && <CheckCircle size={16} weight="fill" className="absolute top-2.5 right-2.5 text-[#C2410C]" />}
                  <div className="flex items-center gap-1.5 pr-5">
                    <span className="text-sm font-semibold text-navy truncate">{p.name}</span>
                    {p.featured && <span className="text-[0.5rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white shrink-0" style={{ background: "#F37A24" }}>Popular</span>}
                  </div>
                  <div className="text-[0.7rem] text-navy/70 leading-tight">
                    {p.enterprise ? "51+ companies" : p.companies === 1 ? "1 company" : `Up to ${p.companies} companies`}
                    {tooSmall && !p.enterprise && <span className="text-warn"> · below usage</span>}
                  </div>
                  <div className="mt-1">
                    {p.enterprise
                      ? <span className="text-sm font-bold text-navy">Custom</span>
                      : <span className="font-heading font-bold text-navy">{gbp(cycle === "annual" ? p.annual : p.monthly)}
                          <span className="text-[0.65rem] font-normal text-navy/65">/{cycle === "annual" ? "yr" : "mo"}</span></span>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Growth pack */}
          {supportsPack && (
            <label className="mt-3 flex items-center gap-2.5 rounded-xl border border-slate-200 px-4 py-3 cursor-pointer hover:border-[#F37A24]/50 transition-colors" data-testid="sub-growth-pack-toggle">
              <input type="checkbox" checked={packs > 0} onChange={(e) => setPacks(e.target.checked ? 1 : 0)} className="accent-[#F37A24] w-4 h-4" />
              <span className="text-sm text-navy flex-1">
                Add a {gp.companies}-Company Growth Pack
                <span className="text-navy/70"> (+{gbp(cycle === "annual" ? gp.annual : gp.monthly)}/{cycle === "annual" ? "yr" : "mo"})</span>
              </span>
            </label>
          )}

          {dirty && downgradeBlocked && (
            <div className="mt-4 rounded-xl border border-[#E5484D]/40 bg-[#E5484D]/[0.06] p-4" data-testid="sub-downgrade-warning">
              <div className="flex items-start gap-2.5">
                <Buildings size={18} weight="fill" className="text-[#E5484D] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-navy">This plan is too small for your practice</div>
                  <p className="text-xs text-navy/70 mt-1">
                    <b>{plan?.name}</b> covers up to {allowance} compan{allowance === 1 ? "y" : "ies"}, but you have <b>{used}</b> connected.
                    Pick a larger plan{supportsPack ? " or add a Growth Pack" : ""}, or disconnect some clients first.
                  </p>
                </div>
              </div>
            </div>
          )}

          {plan?.enterprise ? (
            <a href="mailto:hello@workwithlottie.com?subject=Enterprise%20enquiry" data-testid="sub-contact-sales"
              className="mt-4 w-full sm:w-auto sm:min-w-[220px] inline-flex items-center justify-center font-semibold px-6 py-2.5 rounded-xl border border-navy text-navy hover:bg-navy hover:text-white transition-colors">
              Contact Sales
            </a>
          ) : sub.subscribed ? (
            <div className="mt-5 space-y-3" data-testid="sub-active-block">
              {pastDue && (
                <div className="rounded-xl border border-[#E5484D]/40 bg-[#E5484D]/[0.06] p-4 flex items-start gap-3" data-testid="sub-past-due-band">
                  <CreditCard size={20} weight="fill" className="text-[#E5484D] shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-navy">Your last payment failed</div>
                    <p className="text-xs text-navy/70 mt-1">
                      Update your card and we'll retry the outstanding payment straight away — your access continues in the meantime.
                    </p>
                    {sub.grace_until && (() => {
                      const d = Math.max(0, Math.ceil((new Date(sub.grace_until).getTime() - Date.now()) / 86400000));
                      const urgent = d <= 2;
                      return (
                        <p className={`text-xs font-bold mt-1.5 ${urgent ? "animate-pulse" : ""}`}
                          style={{ color: urgent ? "#7f0d11" : "#B4560F" }} data-testid="sub-grace-countdown">
                          {d === 0 ? "Access ends today if payment isn't taken." : `${d} day${d === 1 ? "" : "s"} of access left before your account is paused.`}
                        </p>
                      );
                    })()}
                    <button onClick={() => openPortal("payment_method_update")} disabled={portalLoading || retrying} data-testid="sub-fix-payment-btn"
                      className="mt-3 inline-flex items-center gap-1.5 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-transform hover:scale-[1.02] disabled:opacity-60"
                      style={{ background: "#E5484D" }}>
                      <CreditCard size={15} weight="fill" /> {retrying ? "Retrying…" : portalLoading ? "Opening…" : "Update card & retry"}
                    </button>
                  </div>
                </div>
              )}
              <div className="rounded-xl border border-positive/30 bg-positive/[0.06] p-4 flex items-start gap-3" data-testid="sub-status-band">
                <CheckCircle size={20} weight="fill" className="text-positive shrink-0 mt-0.5" />
                <div className="text-sm text-navy">
                  <span className="font-semibold">Subscription active</span>
                  {sub.cancel_at_period_end
                    ? <> — cancels on {fmtDate(sub.current_period_end)}.</>
                    : sub.current_period_end && <> — renews on {fmtDate(sub.current_period_end)}.</>}
                  <p className="text-xs text-navy/70 mt-1">{PROVIDER === "adfin" ? "Change your plan below; changes apply from your next billing cycle." : "Change plan, update your card or cancel anytime in the billing portal."}</p>
                </div>
              </div>
              {PROVIDER !== "adfin" && !enterprise && (sub.billing_cycle || "monthly") === "monthly" && !sub.cancel_at_period_end && plan.monthly > 0 && (
                <div className="rounded-xl border border-[#F37A24]/40 bg-[#F37A24]/[0.06] p-4 flex items-start gap-3" data-testid="sub-annual-nudge">
                  <Sparkle size={18} weight="fill" className="text-[#F37A24] shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-navy">Save ~2 months by paying annually</div>
                    <p className="text-xs text-navy/70 mt-1">
                      You're on monthly billing. Switch to annual and save {gbp(plan.monthly * 2 + (sub.growth_packs || 0) * gp.monthly * 2)} a year — same plan, same companies.
                    </p>
                    <button onClick={switchToAnnual} disabled={changing} data-testid="sub-switch-annual-btn"
                      className="mt-3 inline-flex items-center gap-1.5 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-transform hover:scale-[1.02] disabled:opacity-60"
                      style={{ background: "#F37A24" }}>
                      <ArrowUp size={15} weight="bold" /> {changing ? "Switching…" : "Switch to annual"}
                    </button>
                  </div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                {dirty && BILLING_ENABLED && (
                  <button onClick={PROVIDER === "adfin" ? () => setShowAdfin(true) : changePlan} disabled={changing || downgradeBlocked} data-testid="sub-change-plan-btn"
                    className="w-full sm:w-auto sm:min-w-[220px] inline-flex items-center justify-center gap-2 text-white font-semibold px-6 py-2.5 rounded-xl transition-transform hover:scale-[1.01] disabled:opacity-60"
                    style={{ background: "#F37A24" }}>
                    <ArrowUp size={16} weight="bold" /> {changing ? "Updating…" : "Update plan"}
                  </button>
                )}
                {PROVIDER !== "adfin" && (
                  <button onClick={openPortal} disabled={portalLoading} data-testid="sub-manage-billing-btn"
                    className="w-full sm:w-auto sm:min-w-[200px] inline-flex items-center justify-center gap-2 font-semibold px-6 py-2.5 rounded-xl transition-transform hover:scale-[1.01] disabled:opacity-60 border border-navy text-navy hover:bg-navy hover:text-white">
                    <CreditCard size={16} weight="fill" /> {portalLoading ? "Opening…" : "Manage billing"}
                  </button>
                )}
                {PROVIDER === "adfin" && (
                  sub.cancel_at_period_end ? (
                    <button onClick={resumeAdfin} disabled={cancelling} data-testid="sub-resume-btn"
                      className="w-full sm:w-auto sm:min-w-[200px] inline-flex items-center justify-center gap-2 font-semibold px-6 py-2.5 rounded-xl transition-transform hover:scale-[1.01] disabled:opacity-60 border border-positive text-positive hover:bg-positive hover:text-white">
                      <CheckCircle size={16} weight="fill" /> {cancelling ? "Resuming…" : "Resume subscription"}
                    </button>
                  ) : (
                    <button onClick={cancelAdfin} disabled={cancelling} data-testid="sub-cancel-btn"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm font-semibold text-navy/70 hover:text-warn underline underline-offset-2 disabled:opacity-60">
                      {cancelling ? "Cancelling…" : "Cancel subscription"}
                    </button>
                  )
                )}
              </div>
              {dirty && PROVIDER !== "adfin" && <p className="text-xs text-navy/70" data-testid="sub-proration-note">Changing plan applies immediately and is prorated on your next invoice.</p>}
              {dirty && PROVIDER === "adfin" && <p className="text-xs text-navy/70" data-testid="sub-proration-note">Plan changes take effect from your next billing cycle.</p>}
            </div>
          ) : !BILLING_ENABLED ? (
            <div className="mt-5 space-y-3" data-testid="sub-billing-coming-soon">
              <div className="rounded-xl border border-[#F37A24]/30 bg-[#F37A24]/[0.06] p-4 flex items-start gap-3">
                <Sparkle size={20} weight="fill" className="text-[#C2410C] shrink-0 mt-0.5" />
                <div className="text-sm text-navy">
                  <span className="font-semibold">Billing opens soon</span>
                  <p className="text-xs text-navy/70 mt-1">
                    You have full access to every feature during your trial. Online subscriptions aren't quite open yet —
                    we'll email you the moment they are. In the meantime, pick the plan you like and save it for later.
                  </p>
                </div>
              </div>
              {dirty && (
                <button onClick={() => save()} disabled={saving || downgradeBlocked} data-testid="sub-save-btn"
                  className="text-sm font-semibold text-navy/70 hover:text-navy underline underline-offset-2 disabled:opacity-60">
                  {saving ? "Saving…" : "Save my plan selection"}
                </button>
              )}
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <button onClick={onSubscribeClick} disabled={checkingOut || verifying || downgradeBlocked} data-testid="sub-subscribe-btn"
                  className="w-full sm:w-auto sm:min-w-[240px] inline-flex items-center justify-center gap-2 text-white font-semibold px-6 py-2.5 rounded-xl transition-transform hover:scale-[1.01] disabled:opacity-60"
                  style={{ background: "#F37A24" }}>
                  <CreditCard size={16} weight="fill" />
                  {verifying ? "Confirming…" : checkingOut ? "Opening…" : `Subscribe — ${gbp(price)}/${cycle === "annual" ? "yr" : "mo"}`}
                </button>
                {dirty && (
                  <button onClick={() => save()} disabled={saving || downgradeBlocked} data-testid="sub-save-btn"
                    className="text-sm font-semibold text-navy/70 hover:text-navy underline underline-offset-2 disabled:opacity-60">
                    {saving ? "Saving…" : "Save selection for later"}
                  </button>
                )}
              </div>
              {!enterprise && cycle !== "annual" && plan?.monthly > 0 && (
                <button onClick={() => setCycle("annual")} data-testid="sub-annual-nudge"
                  className="w-full sm:w-auto inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-xl bg-positive/[0.08] text-positive hover:bg-positive/[0.14] transition-colors">
                  <Sparkle size={15} weight="fill" />
                  Pay annually and get <b className="font-bold mx-1">2 months free</b> — save {gbp(price * 2)}/yr
                </button>
              )}
            </div>
          )}
        </div>

        {invoices.length > 0 && (
          <div className="mt-6 pt-5 border-t border-slate-100" data-testid="billing-history">
            <div className="flex items-center gap-2 mb-3">
              <Receipt size={16} weight="fill" className="text-navy/65" />
              <span className="overline text-navy/65">Billing history</span>
            </div>
            <div className="rounded-xl border border-slate-100 divide-y divide-slate-50 overflow-hidden">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex items-center gap-3 px-4 py-3" data-testid={`invoice-${inv.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-navy truncate">{inv.number || inv.id}</div>
                    <div className="text-[0.7rem] text-navy/55 capitalize">
                      {fmtDate(inv.created)}{inv.status ? ` · ${inv.status === "paid" ? "Paid" : inv.status}` : ""}
                    </div>
                  </div>
                  <div className="font-heading font-bold text-sm text-navy shrink-0">{gbp(inv.amount_paid || inv.amount_due)}</div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {inv.invoice_pdf && (
                      <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" data-testid={`invoice-pdf-${inv.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-navy/20 text-navy hover:bg-navy/[0.04] transition-colors">
                        <DownloadSimple size={13} weight="bold" /> PDF
                      </a>
                    )}
                    {inv.hosted_invoice_url && (
                      <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" data-testid={`invoice-receipt-${inv.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg text-orangeink hover:bg-orange/[0.08] transition-colors" style={{ color: "#B4560F" }}>
                        <ArrowSquareOut size={13} weight="bold" /> Receipt
                      </a>
                    )}
                    <button onClick={() => resendReceipt(inv.id)} disabled={resendingId === inv.id} data-testid={`invoice-resend-${inv.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-navy/20 text-navy hover:bg-navy/[0.04] transition-colors disabled:opacity-50">
                      <EnvelopeSimple size={13} weight="bold" /> {resendingId === inv.id ? "Sending…" : "Resend"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {showUpsell && !enterprise && plan && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="annual-upsell-modal">
          <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={() => setShowUpsell(false)} />
          <motion.div initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.2 }}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-6">
              <div className="flex items-center gap-2.5 mb-3">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,168,89,0.12)" }}>
                  <Sparkle size={20} weight="fill" style={{ color: "#00A859" }} />
                </span>
                <h3 className="font-heading font-black text-xl text-navy tracking-tight">Save 2 months a year</h3>
              </div>
              <p className="text-sm text-navy/75 leading-relaxed">
                Pay for <b>{plan.name}</b> annually and get <b>2 months free</b> — that's{" "}
                <b className="text-positive" style={{ color: "#00A859" }}>{gbp((plan.monthly * 12 - plan.annual) + packs * (gp.monthly * 12 - gp.annual))}/year</b> off,
                for the same plan and companies.
              </p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-200 p-3 text-center">
                  <div className="overline text-navy/55">Monthly</div>
                  <div className="font-heading font-bold text-navy text-lg">{gbp(plan.monthly + packs * gp.monthly)}<span className="text-xs font-normal text-navy/60">/mo</span></div>
                </div>
                <div className="rounded-xl border-2 border-positive/50 bg-positive/[0.05] p-3 text-center relative">
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[0.6rem] font-bold text-white px-2 py-0.5 rounded-full" style={{ background: "#00A859" }}>BEST VALUE</span>
                  <div className="overline text-navy/55">Annual</div>
                  <div className="font-heading font-bold text-navy text-lg">{gbp(plan.annual + packs * gp.annual)}<span className="text-xs font-normal text-navy/60">/yr</span></div>
                </div>
              </div>
              <div className="mt-5 space-y-2.5">
                <button onClick={() => { setShowUpsell(false); setCycle("annual"); checkout("annual"); }} data-testid="upsell-choose-annual"
                  className="w-full inline-flex items-center justify-center gap-2 text-white font-semibold px-6 py-3 rounded-xl transition-transform hover:scale-[1.01]"
                  style={{ background: "#00A859" }}>
                  <Sparkle size={16} weight="fill" /> Pay annually &amp; save {gbp((plan.monthly * 12 - plan.annual) + packs * (gp.monthly * 12 - gp.annual))}
                </button>
                <button onClick={() => { setShowUpsell(false); checkout("monthly"); }} data-testid="upsell-continue-monthly"
                  className="w-full text-sm font-semibold text-navy/70 hover:text-navy py-2 transition-colors">
                  No thanks, continue monthly
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
      {showAdfin && (
        <AdfinCheckout
          planId={planId}
          cycle={cycle}
          packs={packs}
          planName={cfg?.plans?.find((p) => p.id === planId)?.name}
          onClose={() => { setShowAdfin(false); load(); }}
        />
      )}
    </motion.div>
  );
}
